/**
 * ShortcutsProvider.tsx -- Global keyboard shortcuts provider.
 *
 * PRD SS18.8: 8 keyboard shortcuts (Cmd/Ctrl+K, Cmd/Ctrl+B, Cmd/Ctrl+I,
 * Cmd/Ctrl+., g w, g a, g p, Escape). Chord shortcuts (g + key) have a
 * 500ms timeout window.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useHashLocation } from "../../lib/router/useHashLocation";

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------
interface ShortcutContextValue {
  commandPaletteOpen: boolean;
  leftNavOpen: boolean;
  rightInspectorOpen: boolean;
  moreActionsOpen: boolean;
  toggleCommandPalette: () => void;
  toggleLeftNav: () => void;
  toggleRightInspector: () => void;
  toggleMoreActions: () => void;
}

const ShortcutContext = createContext<ShortcutContextValue | null>(null);

export function useShortcutContext(): ShortcutContextValue {
  const ctx = useContext(ShortcutContext);
  if (!ctx) {
    throw new Error("useShortcutContext must be used within a ShortcutsProvider");
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Chord timeout (ms)
// ---------------------------------------------------------------------------
const CHORD_TIMEOUT_MS = 500;

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
export interface ShortcutsProviderProps {
  children: React.ReactNode;
}

export function ShortcutsProvider({ children }: ShortcutsProviderProps) {
  const [, navigate] = useHashLocation();

  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [leftNavOpen, setLeftNavOpen] = useState(true);
  const [rightInspectorOpen, setRightInspectorOpen] = useState(false);
  const [moreActionsOpen, setMoreActionsOpen] = useState(false);

  // Chord state: "g" pressed, waiting for second key
  const chordPendingRef = useRef(false);
  const chordTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toggleCommandPalette = useCallback(() => {
    setCommandPaletteOpen((prev) => !prev);
  }, []);

  const toggleLeftNav = useCallback(() => {
    setLeftNavOpen((prev) => !prev);
  }, []);

  const toggleRightInspector = useCallback(() => {
    setRightInspectorOpen((prev) => !prev);
  }, []);

  const toggleMoreActions = useCallback(() => {
    setMoreActionsOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isInput =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable;

      // --- Chord shortcuts (g + key) ---
      if (chordPendingRef.current && !isInput) {
        chordPendingRef.current = false;
        if (chordTimerRef.current) {
          clearTimeout(chordTimerRef.current);
          chordTimerRef.current = null;
        }

        switch (e.key) {
          case "w":
            e.preventDefault();
            navigate("/workspaces");
            return;
          case "a":
            e.preventDefault();
            navigate("/insights");
            return;
          case "p":
            e.preventDefault();
            navigate("/pipeline");
            return;
          default:
            // Unknown second key -- ignore
            return;
        }
      }

      // --- "g" chord initiator ---
      if (e.key === "g" && !e.ctrlKey && !e.metaKey && !e.altKey && !isInput) {
        chordPendingRef.current = true;
        chordTimerRef.current = setTimeout(() => {
          chordPendingRef.current = false;
          chordTimerRef.current = null;
        }, CHORD_TIMEOUT_MS);
        return;
      }

      // --- Modified shortcuts (Cmd/Ctrl + key) ---
      const mod = e.ctrlKey || e.metaKey;

      if (mod && e.key === "k") {
        e.preventDefault();
        setCommandPaletteOpen((prev) => !prev);
        return;
      }

      if (mod && e.key === "b") {
        e.preventDefault();
        setLeftNavOpen((prev) => !prev);
        return;
      }

      if (mod && e.key === "i") {
        e.preventDefault();
        setRightInspectorOpen((prev) => !prev);
        return;
      }

      if (mod && e.key === ".") {
        e.preventDefault();
        setMoreActionsOpen((prev) => !prev);
        return;
      }

      // --- Escape ---
      if (e.key === "Escape") {
        // Close the topmost open surface
        if (commandPaletteOpen) {
          setCommandPaletteOpen(false);
          return;
        }
        if (moreActionsOpen) {
          setMoreActionsOpen(false);
          return;
        }
        if (rightInspectorOpen) {
          setRightInspectorOpen(false);
          return;
        }

        // If nothing open, blur the focused control
        if (document.activeElement && document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }
    };

    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
      if (chordTimerRef.current) {
        clearTimeout(chordTimerRef.current);
      }
    };
  }, [commandPaletteOpen, moreActionsOpen, rightInspectorOpen, navigate]);

  const value: ShortcutContextValue = {
    commandPaletteOpen,
    leftNavOpen,
    rightInspectorOpen,
    moreActionsOpen,
    toggleCommandPalette,
    toggleLeftNav,
    toggleRightInspector,
    toggleMoreActions,
  };

  return (
    <ShortcutContext.Provider value={value}>
      {children}
    </ShortcutContext.Provider>
  );
}

export default ShortcutsProvider;
